/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `LogoTelekomInvProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface LogoTelekomInvProps {
    /**
     * A string representing the CSS class to be applied to the LogoTelekomInvIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#fff'
     */
    color1?: string;
    /**
     * A number or string representing the height of the LogoTelekomInvIcon element.
     *
     * @default 44
     */
    height?: number | string;
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'LogoTelekomInv'
     */
    testId?: string;
    /**
     * A number or string representing the height of the LogoTelekomInvIcon element.
     *
     * @default 37
     */
    width?: number | string;
}

/**
 * The `LogoTelekomInv` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `LogoTelekomInvProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the LogoTelekomInvIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the LogoTelekomInvIcon element.
 * @param props.ref       A ref object to access the SVG element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the LogoTelekomInvIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const LogoTelekomInvComponent = <LogoTelekomInv className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const LogoTelekomInv = ({
    className = '',
    color1 = '#fff',
    height = 40,
    ref,
    testId = 'LogoTelekomInv',
    width = 34
}: LogoTelekomInvProps) => (
    <svg
        ref={ref}
        aria-label="Telekom Logo"
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 34 40"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M8.34 26.4H.32v-8h8.02zM.32 0v13.6h2.4v-.4c0-6.4 3.61-10.4 10.43-10.4h.4v28.8c0 4-1.6 5.6-5.61 5.6h-1.2V40h20.84v-2.8h-1.2c-4 0-5.61-1.6-5.61-5.6V2.8h.4c6.82 0 10.42 4 10.42 10.4v.4H34V0zm25.66 26.4H34v-8h-8.02z" fill={color1} />
    </svg>
);

LogoTelekomInv.displayName = 'LogoTelekomInv';